import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaAddressBook,
  FaChartBar,
  FaClock,
  FaEllipsisV,
  FaIdBadge,
  FaListUl,
  FaPen,
  FaPhoneAlt,
  FaPlusCircle,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaFilter,
  FaSync,
  FaUserGraduate,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import AdminModalShell from "./AdminModalShell";
import { useAppTheme } from "../context/AppThemeContext";
import StudentService from "../service/StudentService";
import { getAdminModuleTabs } from "../data/adminModuleTabs";
import { useTenant } from "../context/TenantContext";
import TablePagination from "./TablePagination";

const emptyForm = {
  id: "",
  firstName: "",
  lastName: "",
  fatherOrGuardianName: "",
  motherName: "",
  dateOfBirth: "",
  email: "",
  className: "",
  section: "",
  address: "",
  parentContact: "",
  guardianContact: "",
  studentPhoto: null,
  studentAadhaarCard: null,
  admissionStatus: "ACTIVE",
  feeStatus: "PENDING",
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : "";
};

const normalizeStudent = (record = {}, index = 0) => {
  const profile = record.profile || record.studentProfile || record.studentDetails || {};
  const parent = record.parent || record.guardian || record.parentDetails || {};
  const source = { ...record, ...profile };
  const firstName = source.firstName || source.first_name || source.givenName || "";
  const lastName = source.lastName || source.last_name || source.surname || "";
  return {
  id: record.id || record.studentId || record._id || `student-${index + 1}`,
  firstName,
  lastName,
  name: source.name || source.fullName || source.studentName || [firstName, lastName].filter(Boolean).join(" ") || "Unnamed Student",
  fatherOrGuardianName: source.fatherOrGuardianName || source.fatherName || source.father_name || source.guardianName || source.guardian_name || source.parentName || parent.name || "",
  motherName: source.motherName || source.mother_name || source.motherFullName || source.mother?.name || source.motherDetails?.name || "",
  dateOfBirth: toDateInputValue(source.dateOfBirth || source.date_of_birth || source.birth_date || source.dob || source.birthDate || source.birth_date_value),
  email: source.email || "",
  className: source.className || source.class_name || source.studentClass || source.standard || "Class",
  section: source.section || "A",
  rollNo: record.rollNo || record.rollNumber || "NA",
  address: source.address || source.residentialAddress || source.residential_address || source.homeAddress || "",
  parentName: source.fatherOrGuardianName || source.fatherName || source.guardianName || source.parentName || parent.name || "Not added",
  parentContact: source.parentContact || source.parent_phone || source.parentPhone || source.contactNumber || source.emergencyContact || parent.mobile || parent.phone || "",
  guardianContact: source.guardianContact || source.guardianNumber || source.guardian_phone || parent.guardianContact || "",
  admissionStatus: String(record.admissionStatus || record.status || "NA").toUpperCase(),
  feeStatus: String(record.feeStatus || record.paymentStatus || "NA").toUpperCase(),
  };
};

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "";
  const birthDate = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(birthDate.getTime()) || birthDate > new Date()) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasNotHadBirthday = today.getMonth() < birthDate.getMonth()
    || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate());
  if (hasNotHadBirthday) age -= 1;
  return String(age);
};

const isValidPhoneNumber = (value) => /^\d{10}$/.test(value);
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const nameFields = ["firstName", "lastName", "fatherOrGuardianName", "motherName"];
const FieldError = ({ message }) => message ? <p className="mt-1 text-xs text-rose-600">{message}</p> : null;

const validateStudentForm = (form, { isEditing = false } = {}) => {
  const errors = {};
  nameFields.forEach((field) => {
    const value = form[field].trim();
    if (!value) errors[field] = "This field is required.";
    else if (value.length < 2 || value.length > 60) errors[field] = "Use 2 to 60 characters.";
  });
  if (!form.dateOfBirth) errors.dateOfBirth = "Date of birth is required.";
  else if (!calculateAge(form.dateOfBirth)) errors.dateOfBirth = "Enter a valid past date.";
  if (form.email.trim() && !isValidEmail(form.email.trim())) errors.email = "Enter a valid email address.";
  ["className", "section", "address"].forEach((field) => {
    const value = form[field].trim();
    if (!value) errors[field] = "This field is required.";
    else if (value.length < (field === "section" ? 1 : 2) || value.length > (field === "address" ? 250 : 30)) errors[field] = `Use ${field === "section" ? 1 : 2} to ${field === "address" ? 250 : 30} characters.`;
  });
  if (!form.parentContact.trim()) errors.parentContact = "Parent contact number is required.";
  else if (!isValidPhoneNumber(form.parentContact.trim())) errors.parentContact = "Enter exactly 10 digits.";
  if (form.guardianContact.trim() && !isValidPhoneNumber(form.guardianContact.trim())) errors.guardianContact = "Enter exactly 10 digits.";
  return errors;
};

const extractStudents = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const statusTone = (value, isDarkMode) => {
  if (value === "PAID" || value === "ACTIVE") {
    return isDarkMode ? "border-emerald-900/70 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (value === "REVIEW" || value === "PENDING" || value === "PARTIAL") {
    return isDarkMode ? "border-amber-900/70 bg-amber-950/40 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-700";
  }
  return isDarkMode ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600";
};

const studentInitials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "S";

const SortableColumnHeader = ({ label, field, sortBy, onSort, className = "" }) => {
  const [activeField, direction] = sortBy.split("-");
  const isActive = activeField === field;
  const SortIcon = !isActive ? FaSort : direction === "asc" ? FaSortUp : FaSortDown;
  return (
    <th className={`px-4 py-3 text-left ${className}`}>
      <button type="button" onClick={() => onSort(field)} className="inline-flex items-center gap-2 font-semibold hover:text-emerald-600" title={`Sort by ${label}`}>
        {label}<SortIcon className={isActive ? "text-emerald-500" : "text-slate-400"} />
      </button>
    </th>
  );
};

const StudentMobileCard = ({ student, isDarkMode, parentMode = false, onUpdate, onDelete, isDeleting }) => (
  <article className={`overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
    <div className={`flex items-start justify-between gap-3 px-4 py-4 ${isDarkMode ? "bg-slate-900" : "bg-gradient-to-r from-emerald-50 to-white"}`}>
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500 text-sm font-black text-white shadow-sm">{studentInitials(student.name)}</span>
        <div className="min-w-0"><p className="truncate font-bold">{student.name}</p><p className="mt-0.5 truncate text-xs text-slate-500">{student.email || "No email added"}</p></div>
      </div>
      <div className="flex shrink-0 gap-2"><button type="button" onClick={() => onUpdate(student.id)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${isDarkMode ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"}`}>Update</button><button type="button" disabled={isDeleting} onClick={() => onDelete(student)} className="rounded-xl border border-rose-300 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-60">{isDeleting ? "Deleting..." : "Delete"}</button></div>
    </div>
    <div className="grid grid-cols-2 gap-px bg-slate-100 dark:bg-slate-800">
      <div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Class / section</p><p className="mt-1 text-sm font-semibold">{student.className} - {student.section}</p></div>
      <div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Roll number</p><p className="mt-1 text-sm font-semibold">{student.rollNo}</p></div>
      {parentMode ? <><div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Parent</p><p className="mt-1 truncate text-sm font-semibold">{student.parentName}</p></div><div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Contact</p><p className="mt-1 text-sm font-semibold">{student.parentContact}</p></div></> : <><div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Admission</p><span className={`mt-1.5 inline-flex rounded-full border px-2 py-1 text-[10px] font-bold ${statusTone(student.admissionStatus, isDarkMode)}`}>{student.admissionStatus}</span></div><div className={`px-4 py-3 ${isDarkMode ? "bg-slate-950" : "bg-white"}`}><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Fees</p><span className={`mt-1.5 inline-flex rounded-full border px-2 py-1 text-[10px] font-bold ${statusTone(student.feeStatus, isDarkMode)}`}>{student.feeStatus}</span></div></>}
    </div>
  </article>
);

const AdminStudentManagement = () => {
  const { isDarkMode } = useAppTheme();
  const { tenantFeatures } = useTenant();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [admissionStatusFilter, setAdmissionStatusFilter] = useState("ALL");
  const [feeStatusFilter, setFeeStatusFilter] = useState("ALL");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [activeView, setActiveView] = useState("directory");
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [openActionsFor, setOpenActionsFor] = useState("");
  const [deletingStudentId, setDeletingStudentId] = useState("");
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryPageSize, setDirectoryPageSize] = useState(10);
  const studentDetailCacheRef = useRef(new Map());
  const editRequestIdRef = useRef(0);

  const fetchStudents = async ({ forceRefresh = false, withLoader = true } = {}) => {
    if (withLoader) {
      setLoading(true);
    }

    try {
      const nextStudents = (await StudentService.getStudents({ forceRefresh })).map(normalizeStudent);
      setStudents(nextStudents);
      setSelectedStudentId(nextStudents[0]?.id || "");
      return true;
    } catch (error) {
      setStudents([]);
      setSelectedStudentId("");
      toast.error(error?.message || "Unable to load student records.");
      return false;
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleRefreshStudents = async () => {
    const didRefresh = await fetchStudents({ forceRefresh: true, withLoader: true });
    if (didRefresh) {
      toast.success("Student records refreshed.");
    }
  };

  const filteredStudents = useMemo(() => {
    const searchTokens = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matching = students.filter((student) => (
      searchTokens.length === 0 || searchTokens.every((token) => [
        student.name,
        student.email,
        student.className,
        student.section,
        student.rollNo,
        student.parentName,
        student.parentContact,
        student.admissionStatus,
        student.feeStatus,
      ].some((value) => String(value || "").toLowerCase().includes(token)))
    ));
    const statusFiltered = matching.filter((student) => (
      (activeView === "parents" || admissionStatusFilter === "ALL" || student.admissionStatus === admissionStatusFilter)
      && (activeView === "parents" || feeStatusFilter === "ALL" || student.feeStatus === feeStatusFilter)
    ));
    return [...statusFiltered].sort((left, right) => {
      const [field, direction] = sortBy.split("-");
      const fieldMap = { name: "name", class: "className", section: "section", roll: "rollNo", admission: "admissionStatus", fees: "feeStatus" };
      const key = fieldMap[field] || "name";
      const result = String(left[key] || "").localeCompare(String(right[key] || ""), undefined, { numeric: true, sensitivity: "base" });
      return direction === "desc" ? -result : result;
    });
  }, [activeView, admissionStatusFilter, feeStatusFilter, searchTerm, sortBy, students]);

  const admissionStatuses = useMemo(() => [...new Set(students.map((student) => student.admissionStatus).filter(Boolean))].sort(), [students]);
  const feeStatuses = useMemo(() => [...new Set(students.map((student) => student.feeStatus).filter(Boolean))].sort(), [students]);

  const summary = useMemo(() => ({
    total: students.length,
    active: students.filter((student) => student.admissionStatus === "ACTIVE").length,
    review: students.filter((student) => student.admissionStatus === "REVIEW").length,
    pendingFees: students.filter((student) => student.feeStatus !== "PAID").length,
  }), [students]);

  const recentActivity = useMemo(() => students.slice(0, 8), [students]);
  const visibleStudents = filteredStudents;
  const paginatedStudents = useMemo(() => visibleStudents.slice((directoryPage - 1) * directoryPageSize, directoryPage * directoryPageSize), [visibleStudents, directoryPage, directoryPageSize]);

  useEffect(() => { setDirectoryPage(1); }, [activeView, admissionStatusFilter, feeStatusFilter, searchTerm, sortBy, directoryPageSize]);

  const panelClass = isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60";
  const handleColumnSort = (field) => {
    setSortBy((current) => {
      const [currentField, currentDirection] = current.split("-");
      return currentField === field && currentDirection === "asc" ? `${field}-desc` : `${field}-asc`;
    });
  };
  const inputClass = (field) => `w-full rounded-2xl border px-3 py-3 text-sm focus:outline-none focus:ring-2 ${formErrors[field] ? "border-rose-500 focus:border-rose-500 focus:ring-rose-100" : "border-slate-300 focus:border-emerald-500 focus:ring-emerald-100"}`;

  const resetForm = () => {
    setSelectedStudentId("");
    setEditingStudentId("");
    setIsEditLoading(false);
    setForm(emptyForm);
    setFormErrors({});
  };

  const handleDeleteStudent = async (student) => {
    if (!window.confirm(`Delete ${student.name}? This will deactivate the student record and remove it from this directory.`)) return;

    setDeletingStudentId(student.id);
    try {
      await api.delete(API_PATHS.admin.studentById(student.id));
      setStudents((current) => current.filter((item) => item.id !== student.id));
      setSelectedStudentId((current) => current === student.id ? "" : current);
      studentDetailCacheRef.current.delete(student.id);
      StudentService.invalidateStudentsCache();
      toast.success("Student deleted.");
    } catch (error) {
      toast.error(error?.message || "Unable to delete student.");
    } finally {
      setDeletingStudentId("");
    }
  };

  const openCreateForm = () => {
    navigate("/admin/students/create");
  };

  const closeStudentForm = () => {
    editRequestIdRef.current += 1;
    setIsFormOpen(false);
    setIsEditLoading(false);
    setEditingStudentId("");
  };

  const openEditForm = async (studentId) => {
    const student = students.find((record) => record.id === studentId) || null;
    navigate(`/admin/students/${encodeURIComponent(String(studentId))}/edit`, {
      state: { student },
    });
    return;

    const requestId = ++editRequestIdRef.current;
    const existingStudent = students.find((student) => student.id === studentId) || null;
    const cachedStudent = studentDetailCacheRef.current.get(studentId);
    setSelectedStudentId(studentId);
    setEditingStudentId(studentId);
    setIsFormOpen(true);
    setFormErrors({});

    // Do not first render a partial row record. That made fields appear to
    // populate one-by-one when the detail request completed.
    if (cachedStudent) {
      setForm({ ...emptyForm, ...cachedStudent });
      setIsEditLoading(false);
      return;
    }

    setForm(emptyForm);
    setIsEditLoading(true);
    try {
      const response = await api.get(API_PATHS.admin.studentById(studentId));
      const payload = response?.data?.data ?? response?.data ?? {};
      const detail = payload.student || payload.studentDetails || payload.result || payload.content || payload;
      if (!detail || typeof detail !== "object") return;
      const detailStudent = normalizeStudent({ ...existingStudent, ...detail, id: studentId }, students.length);
      studentDetailCacheRef.current.set(studentId, detailStudent);
      setStudents((current) => current.map((student) => (
        student.id === studentId ? { ...student, ...detailStudent } : student
      )));
      if (requestId === editRequestIdRef.current) {
        setForm({ ...emptyForm, ...detailStudent });
      }
    } catch (error) {
      // A list response can be edited as a compatibility fallback, but make
      // that state explicit instead of presenting it as a fully loaded form.
      if (requestId === editRequestIdRef.current && existingStudent) {
        setForm({ ...emptyForm, ...existingStudent });
        toast.error(error?.message || "Unable to load the complete student record. Showing available details.");
      }
    } finally {
      if (requestId === editRequestIdRef.current) {
        setIsEditLoading(false);
      }
    }
  };

  const updateForm = (key, value) => {
    const nextValue = key === "parentContact" || key === "guardianContact" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setForm((current) => ({ ...current, [key]: nextValue }));
    setFormErrors((current) => ({ ...current, [key]: "" }));
  };

  const upsertStudent = (nextStudent) => {
    setStudents((current) => {
      const exists = current.some((student) => student.id === nextStudent.id);
      if (!exists) return [nextStudent, ...current];
      return current.map((student) => (student.id === nextStudent.id ? nextStudent : student));
    });
    setSelectedStudentId(nextStudent.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const isEditing = Boolean(form.id);
    const errors = validateStudentForm(form, { isEditing });
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      toast.error("Please correct the highlighted fields.");
      return;
    }

    setSaving(true);
    const age = calculateAge(form.dateOfBirth);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      fatherOrGuardianName: form.fatherOrGuardianName.trim(),
      motherName: form.motherName.trim(),
      dateOfBirth: form.dateOfBirth,
      age: Number(age),
      name: `${form.firstName.trim()} ${form.lastName.trim()}`,
      email: form.email.trim(),
      className: form.className.trim(),
      section: form.section.trim(),
      address: form.address.trim(),
      parentName: form.fatherOrGuardianName.trim(),
      parentContact: form.parentContact.trim(),
      guardianContact: form.guardianContact.trim(),
      admissionStatus: form.admissionStatus,
      feeStatus: form.feeStatus,
    };

    try {
      let response;
      if (isEditing) {
        response = await api.put(API_PATHS.admin.studentById(form.id), payload);
      } else {
        const requestData = new FormData();
        Object.entries(payload).forEach(([key, value]) => requestData.append(key, String(value)));
        if (form.studentPhoto) requestData.append("studentPhoto", form.studentPhoto);
        if (form.studentAadhaarCard) requestData.append("studentAadhaarCard", form.studentAadhaarCard);
        response = await api.post(API_PATHS.student.createStudent(), requestData);
      }
      StudentService.invalidateStudentsCache();
      const nextStudent = normalizeStudent({ ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined }, students.length);
      if (isEditing) studentDetailCacheRef.current.set(form.id, nextStudent);
      upsertStudent(nextStudent);
      closeStudentForm();
      toast.success(isEditing ? "Student updated." : "Student created.");
    } catch (error) {
      toast.error(error?.message || "Unable to save student record.");
    } finally {
      setSaving(false);
    }
  };

  const activityTabs = useMemo(() => {
    const registeredTabs = getAdminModuleTabs("/admin/students");
    const studentFeatures = tenantFeatures?.students;
    if (!studentFeatures) return registeredTabs;
    if (!studentFeatures.enabled) return [];
    return registeredTabs.filter((tab) => studentFeatures.tabs?.[tab.key] === true);
  }, [tenantFeatures]);

  useEffect(() => {
    if (activityTabs.length && !activityTabs.some((tab) => tab.key === activeView)) {
      setActiveView(activityTabs[0].key);
    }
  }, [activityTabs, activeView]);

  return (
    <div className={isDarkMode ? "text-slate-100" : "text-slate-900"}>
      <div className="space-y-0">
        <section className="space-y-0">
          <div className={`grid grid-cols-3 gap-2 px-3 py-3 sm:flex sm:flex-wrap sm:items-center sm:px-6 ${isDarkMode ? "bg-slate-900" : "bg-slate-50/80"}`}>
            {activityTabs.map((tab) => {
              const isActive = activeView === tab.key;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveView(tab.key)}
                    className={`relative flex min-h-[48px] flex-col items-center justify-center gap-0.5 rounded-xl px-1.5 py-1.5 text-[9px] font-semibold transition-all duration-200 sm:inline-flex sm:min-h-0 sm:flex-row sm:gap-1 sm:rounded-full sm:px-4 sm:py-2 sm:text-sm ${
                      isActive
                        ? isDarkMode
                        ? "bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-950/30"
                        : "bg-emerald-500 text-white shadow-md shadow-emerald-200"
                      : isDarkMode
                        ? "bg-slate-800/80 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                        : "bg-white text-slate-500 shadow-sm hover:bg-emerald-50 hover:text-emerald-700"
                  }`}
                >
                  {TabIcon && <TabIcon className="text-[13px] sm:text-xs" />}
                  <span className="sm:hidden">{tab.mobileLabel || tab.label}</span><span className="hidden sm:inline">{tab.label}</span>
                  {tab.key === "activity" && (
                    <span className={`absolute right-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold sm:static sm:ml-2 sm:rounded-none sm:text-[10px] ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-600"}`}>
                      {recentActivity.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{activeView === "metrics" ? "Metrics" : "Student workspace"}</p>
                <h2 className="mt-2 text-2xl font-bold">
                  {activeView === "directory" && "Records and placement"}
                  {activeView === "metrics" && "Student module metrics"}
                  {activeView === "admission" && "Admission submodule"}
                  {activeView === "fees" && "Fee tracker submodule"}
                  {activeView === "parents" && "Parent contact submodule"}
                  {activeView === "activity" && "Student activity submodule"}
                </h2>
              </div>

              {(activeView === "directory" || activeView === "admission" || activeView === "fees" || activeView === "parents" || activeView === "activity") && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className={`inline-flex overflow-hidden rounded-full p-1 md:hidden ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`} aria-label="Choose student record layout">
                    {[["cards", "Cards"], ["table", "Table"]].map(([value, label]) => <button key={value} type="button" onClick={() => setMobileRecordView(value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${mobileRecordView === value ? "bg-emerald-500 text-white shadow-sm" : isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{label}</button>)}
                  </div>
                  <button
                    type="button"
                    onClick={handleRefreshStudents}
                    disabled={loading}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${isDarkMode ? "border border-slate-700 text-slate-200 hover:bg-slate-800" : "border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
                  >
                    <FaSync className={loading ? "animate-spin" : ""} /> Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/students/import")}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${isDarkMode ? "border border-emerald-700/70 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" : "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                  >
                    <FaPlusCircle /> Import students
                  </button>
                  <button
                    type="button"
                    onClick={openCreateForm}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"
                  >
                    <FaPlusCircle /> Create student
                  </button>
                </div>
              )}
            </div>

            {(activeView === "directory" || activeView === "admission" || activeView === "fees" || activeView === "parents") && (
              <div className="mt-5">
                <div className="relative">
                  <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 py-3 pl-10 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    placeholder="Search name, class, section, roll, parent, admission or fee status"
                  />
                </div>
              </div>
            )}

            <div className="mt-6 space-y-4">
              {loading ? (
                <div className={`rounded-2xl border px-4 py-10 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  Loading student records...
                </div>
              ) : null}

              {!loading && activeView === "metrics" && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className={`border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}><p className="text-xs text-slate-400">Students</p><p className="mt-2 text-2xl font-bold">{summary.total}</p></div>
                    <div className={`border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}><p className="text-xs text-slate-400">Active</p><p className="mt-2 text-2xl font-bold">{summary.active}</p></div>
                    <div className={`border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}><p className="text-xs text-slate-400">In review</p><p className="mt-2 text-2xl font-bold">{summary.review}</p></div>
                    <div className={`border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}><p className="text-xs text-slate-400">Pending fees</p><p className="mt-2 text-2xl font-bold">{summary.pendingFees}</p></div>
                  </div>
                  <div className={`overflow-hidden border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                          <tr>
                            <th className="px-4 py-3 text-left">Metric</th>
                            <th className="px-4 py-3 text-left">Value</th>
                          </tr>
                        </thead>
                        <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                          <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Total students</td><td className="px-4 py-3">{summary.total}</td></tr>
                          <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Active admissions</td><td className="px-4 py-3">{summary.active}</td></tr>
                          <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Review admissions</td><td className="px-4 py-3">{summary.review}</td></tr>
                          <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Pending/partial fees</td><td className="px-4 py-3">{summary.pendingFees}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!loading && activeView === "activity" && (
                <>
                {mobileRecordView === "cards" && <div className="space-y-3 md:hidden">
                  {recentActivity.map((student) => (
                    <StudentMobileCard key={student.id} student={student} isDarkMode={isDarkMode} onUpdate={openEditForm} onDelete={handleDeleteStudent} isDeleting={deletingStudentId === student.id} />
                  ))}
                </div>}
                <div className={`overflow-hidden border ${mobileRecordView === "cards" ? "hidden md:block" : "block"} ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                  <div className="overflow-x-auto">
                    <table className="min-w-[1080px] w-full table-fixed text-sm">
                      <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                        <tr>
                          <th className="px-4 py-3 text-left">Student</th>
                          <th className="px-4 py-3 text-left">Class</th>
                          <th className="px-4 py-3 text-left">Admission</th>
                          <th className="px-4 py-3 text-left">Fees</th>
                        </tr>
                      </thead>
                      <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                        {recentActivity.map((student) => (
                          <tr key={student.id} className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                            <td className="px-4 py-3">{student.name}</td>
                            <td className="px-4 py-3">{student.className}-{student.section}</td>
                            <td className="px-4 py-3">{student.admissionStatus}</td>
                            <td className="px-4 py-3">{student.feeStatus}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              )}

              {!loading && (activeView === "directory" || activeView === "admission" || activeView === "fees" || activeView === "parents") && (
                <>
                {activeView !== "parents" && (admissionStatusFilter !== "ALL" || feeStatusFilter !== "ALL") && (
                  <div className={`flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                    <span className="text-xs font-semibold text-slate-500">Active filters</span>
                    {admissionStatusFilter !== "ALL" && (
                      <button type="button" onClick={() => setAdmissionStatusFilter("ALL")} className={isDarkMode ? "inline-flex items-center gap-2 rounded-full bg-emerald-950 px-3 py-1.5 text-xs font-bold text-emerald-300" : "inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-800"}>
                        Admission: {admissionStatusFilter}<span aria-hidden="true">×</span>
                      </button>
                    )}
                    {feeStatusFilter !== "ALL" && (
                      <button type="button" onClick={() => setFeeStatusFilter("ALL")} className={isDarkMode ? "inline-flex items-center gap-2 rounded-full bg-amber-950 px-3 py-1.5 text-xs font-bold text-amber-300" : "inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800"}>
                        Fees: {feeStatusFilter}<span aria-hidden="true">×</span>
                      </button>
                    )}
                    <button type="button" onClick={() => { setAdmissionStatusFilter("ALL"); setFeeStatusFilter("ALL"); }} className="ml-auto text-xs font-semibold text-slate-500 underline-offset-2 hover:text-rose-600 hover:underline">Clear all</button>
                  </div>
                )}
                {mobileRecordView === "cards" && <div className="space-y-3 md:hidden">
                  {paginatedStudents.map((student) => (
                    <StudentMobileCard key={student.id} student={student} isDarkMode={isDarkMode} parentMode={activeView === "parents"} onUpdate={openEditForm} onDelete={handleDeleteStudent} isDeleting={deletingStudentId === student.id} />
                  ))}
                  {visibleStudents.length === 0 && <p className="rounded-2xl border border-dashed p-6 text-center text-sm text-slate-500">No student records found.</p>}
                </div>}
                <div className={`overflow-hidden rounded-2xl border ${mobileRecordView === "cards" ? "hidden md:block" : "block"} ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                        <tr>
                          <SortableColumnHeader label="Name" field="name" sortBy={sortBy} onSort={handleColumnSort} />
                          <SortableColumnHeader label="Class" field="class" sortBy={sortBy} onSort={handleColumnSort} />
                          <SortableColumnHeader label="Section" field="section" sortBy={sortBy} onSort={handleColumnSort} />
                          <SortableColumnHeader label="Roll" field="roll" sortBy={sortBy} onSort={handleColumnSort} />
                          {activeView === "parents" ? (
                            <>
                              <th className="px-4 py-3 text-left">Parent</th>
                              <th className="px-4 py-3 text-left">Contact</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 text-left">
                                <div className="flex min-w-[9rem] items-center gap-2">
                                  <button type="button" onClick={() => handleColumnSort("admission")} className="inline-flex items-center gap-1.5 font-semibold hover:text-emerald-600">Admission{sortBy.startsWith("admission-") ? (sortBy.endsWith("asc") ? <FaSortUp /> : <FaSortDown />) : <FaSort className="text-slate-400" />}</button>
                                  <span className={`relative grid h-7 w-7 shrink-0 place-items-center rounded-md border transition ${admissionStatusFilter === "ALL" ? (isDarkMode ? "border-slate-700 text-slate-400 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-500 hover:bg-slate-100") : (isDarkMode ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-emerald-300 bg-emerald-100 text-emerald-700")}`} title="Filter admission status">
                                    <FaFilter className="text-xs" aria-hidden="true" />
                                    {admissionStatusFilter !== "ALL" && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-slate-900" />}
                                    <select aria-label="Filter by admission status" value={admissionStatusFilter} onChange={(event) => setAdmissionStatusFilter(event.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0">
                                    <option value="ALL">All</option>
                                    {admissionStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                  </span>
                                </div>
                              </th>
                              <th className="px-4 py-3 text-left">
                                <div className="flex min-w-[7rem] items-center gap-2">
                                  <button type="button" onClick={() => handleColumnSort("fees")} className="inline-flex items-center gap-1.5 font-semibold hover:text-emerald-600">Fees{sortBy.startsWith("fees-") ? (sortBy.endsWith("asc") ? <FaSortUp /> : <FaSortDown />) : <FaSort className="text-slate-400" />}</button>
                                  <span className={`relative grid h-7 w-7 shrink-0 place-items-center rounded-md border transition ${feeStatusFilter === "ALL" ? (isDarkMode ? "border-slate-700 text-slate-400 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-500 hover:bg-slate-100") : (isDarkMode ? "border-amber-700 bg-amber-950 text-amber-300" : "border-amber-300 bg-amber-100 text-amber-700")}`} title="Filter fee status">
                                    <FaFilter className="text-xs" aria-hidden="true" />
                                    {feeStatusFilter !== "ALL" && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-500 dark:border-slate-900" />}
                                    <select aria-label="Filter by fee status" value={feeStatusFilter} onChange={(event) => setFeeStatusFilter(event.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0">
                                    <option value="ALL">All</option>
                                    {feeStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                  </span>
                                </div>
                              </th>
                            </>
                          )}
                          <th className="w-[8%] px-4 py-3 text-right"><span className="sr-only">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                        {paginatedStudents.map((student) => (
                          <tr key={student.id} className={`border-t transition ${isDarkMode ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-200 hover:bg-slate-50"}`}>
                            <td className="px-4 py-3"><div className="flex items-center gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{studentInitials(student.name)}</span><div className="min-w-0"><p className="font-semibold">{student.name}</p><p className={`truncate text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{student.email || "No email added"}</p></div></div></td>
                            <td className="px-4 py-3">{student.className}</td>
                            <td className="px-4 py-3">{student.section}</td>
                            <td className="px-4 py-3">{student.rollNo}</td>
                            {activeView === "parents" ? (
                              <>
                                <td className="px-4 py-3">{student.parentName}</td>
                                <td className="px-4 py-3">{student.parentContact}</td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${statusTone(student.admissionStatus, isDarkMode)}`}>{student.admissionStatus}</span></td>
                                <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${statusTone(student.feeStatus, isDarkMode)}`}>{student.feeStatus}</span></td>
                              </>
                            )}
                            <td className="relative px-4 py-3 text-right"><button type="button" aria-label={`Actions for ${student.name}`} onClick={() => setOpenActionsFor((current) => current === student.id ? "" : student.id)} className={`rounded-lg p-2 transition ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}><FaEllipsisV /></button>{openActionsFor === student.id && <div className={`absolute right-4 z-10 mt-1 w-44 rounded-xl border p-1 text-left shadow-lg ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}><button type="button" onClick={() => { setOpenActionsFor(""); openEditForm(student.id); }} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}>View / Edit Student</button><button type="button" onClick={() => { setOpenActionsFor(""); setActiveView("parents"); }} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${isDarkMode ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}>View Parent Details</button><button type="button" disabled={deletingStudentId === student.id} onClick={() => { setOpenActionsFor(""); handleDeleteStudent(student); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-60">{deletingStudentId === student.id ? "Deleting..." : "Delete student"}</button></div>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TablePagination totalItems={visibleStudents.length} page={directoryPage} pageSize={directoryPageSize} onPageChange={setDirectoryPage} onPageSizeChange={setDirectoryPageSize} />
                </>
              )}
            </div>
          </div>

          <AdminModalShell isOpen={isFormOpen} onClose={closeStudentForm} isDarkMode={isDarkMode}>
          <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
            <div className="flex items-center justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student form</p><h2 className="mt-2 text-2xl font-bold">{editingStudentId || form.id ? "Update student" : "Create student"}</h2></div></div>
            {isEditLoading ? (
              <div className="flex min-h-72 items-center justify-center text-sm font-medium text-slate-500" role="status">Loading complete student details…</div>
            ) : <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-sm font-medium">First name *</label><input type="text" value={form.firstName} onChange={(event) => updateForm("firstName", event.target.value)} className={inputClass("firstName")} minLength="2" maxLength="60" placeholder="First name" /><FieldError message={formErrors.firstName} /></div>
                <div><label className="mb-1 block text-sm font-medium">Last name *</label><input type="text" value={form.lastName} onChange={(event) => updateForm("lastName", event.target.value)} className={inputClass("lastName")} minLength="2" maxLength="60" placeholder="Last name" /><FieldError message={formErrors.lastName} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-sm font-medium">Father / guardian name *</label><input type="text" value={form.fatherOrGuardianName} onChange={(event) => updateForm("fatherOrGuardianName", event.target.value)} className={inputClass("fatherOrGuardianName")} minLength="2" maxLength="60" placeholder="Father or guardian name" /><FieldError message={formErrors.fatherOrGuardianName} /></div>
                <div><label className="mb-1 block text-sm font-medium">Mother name *</label><input type="text" value={form.motherName} onChange={(event) => updateForm("motherName", event.target.value)} className={inputClass("motherName")} minLength="2" maxLength="60" placeholder="Mother name" /><FieldError message={formErrors.motherName} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><label className="mb-1 block text-sm font-medium">Date of birth *</label><input type="date" value={form.dateOfBirth} onChange={(event) => updateForm("dateOfBirth", event.target.value)} max={new Date().toISOString().slice(0, 10)} className={inputClass("dateOfBirth")} /><FieldError message={formErrors.dateOfBirth} /></div>
                <div><label className="mb-1 block text-sm font-medium">Age</label><input type="text" value={calculateAge(form.dateOfBirth)} readOnly aria-readonly="true" tabIndex="-1" className="w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3 text-sm text-slate-500" placeholder="Calculated from date of birth" /><p className="mt-1 text-xs text-slate-500">Calculated automatically.</p></div>
                <div><label className="mb-1 block text-sm font-medium">Email</label><input type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} className={inputClass("email")} maxLength="120" placeholder="student@school.com" /><FieldError message={formErrors.email} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-sm font-medium">Class *</label><input type="text" value={form.className} onChange={(event) => updateForm("className", event.target.value)} className={inputClass("className")} maxLength="30" placeholder="Class 8" /><FieldError message={formErrors.className} /></div>
                <div><label className="mb-1 block text-sm font-medium">Section *</label><input type="text" value={form.section} onChange={(event) => updateForm("section", event.target.value)} className={inputClass("section")} maxLength="30" placeholder="A" /><FieldError message={formErrors.section} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-sm font-medium">Parent contact number *</label><input type="tel" inputMode="numeric" value={form.parentContact} onChange={(event) => updateForm("parentContact", event.target.value)} className={inputClass("parentContact")} maxLength="10" placeholder="10-digit number" /><FieldError message={formErrors.parentContact} /></div>
                <div><label className="mb-1 block text-sm font-medium">Guardian contact number</label><input type="tel" inputMode="numeric" value={form.guardianContact} onChange={(event) => updateForm("guardianContact", event.target.value)} className={inputClass("guardianContact")} maxLength="10" placeholder="10-digit number" /><FieldError message={formErrors.guardianContact} /></div>
              </div>
              <div><label className="mb-1 block text-sm font-medium">Address *</label><textarea value={form.address} onChange={(event) => updateForm("address", event.target.value)} className={inputClass("address")} minLength="2" maxLength="250" rows="3" placeholder="Residential address" /><FieldError message={formErrors.address} /></div>
                {!form.id && <div className="grid gap-4 sm:grid-cols-2"><div><label className="mb-1 block text-sm font-medium">Student photo <span className="text-slate-500">(optional)</span></label><input type="file" accept="image/*" onChange={(event) => updateForm("studentPhoto", event.target.files?.[0] || null)} className={inputClass("studentPhoto")} /></div><div><label className="mb-1 block text-sm font-medium">Student Aadhaar card <span className="text-slate-500">(optional)</span></label><input type="file" accept="image/*,.pdf" onChange={(event) => updateForm("studentAadhaarCard", event.target.files?.[0] || null)} className={inputClass("studentAadhaarCard")} /></div></div>}
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Admission status</label><select value={form.admissionStatus} onChange={(event) => updateForm("admissionStatus", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"><option value="NA">NA</option><option value="ACTIVE">Active</option><option value="REVIEW">Review</option><option value="HOLD">Hold</option></select></div>
                <div><label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Fee status</label><select value={form.feeStatus} onChange={(event) => updateForm("feeStatus", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"><option value="NA">NA</option><option value="PAID">Paid</option><option value="PENDING">Pending</option><option value="PARTIAL">Partial</option></select></div>
              </div>
              <div className="flex flex-wrap gap-3"><button type="submit" disabled={saving} className="inline-flex items-center rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-70">{saving ? "Saving..." : form.id ? "Update student" : "Create student"}</button><button type="button" onClick={closeStudentForm} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Cancel</button></div>
            </form>}
          </div>
          </AdminModalShell>
        </section>
      </div>
    </div>
  );
};

export default AdminStudentManagement;
